import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { Save, X } from "lucide-react";
import { createCamara, updateCamaraById } from "../../camaraManager/api/camaraManager.api";
import { getSlotQuotaMessage } from "../../utils/slotQuota.js";
import LocationPickerMap from "./CoordPicker";

export interface PendingCamara {
  id?: string;
  internalID?: string;
  Name?: string;
  feedID: string;
  identifier?: string;
  city?: string;
  street?: string;
  orientation?: string;
  customerId?: string;
  active?: boolean;
  status?: 'Creado' | 'Aprobado' | 'Terminado' | 'Suspendida' | '';
  feedDays?: { totalDays: number; daysLeft: number; createdAt: string } | null;
  coordinates?: { lat: number; lng: number } | string | null;
  [key: string]: any;
}

interface PendingCamaraFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  editingCamara?: PendingCamara | null;
  initialCustomerId?: string;
  onSaved?: () => void;
}

const emptyForm = (initialCustomerId?: string): PendingCamara => ({
  internalID: "",
  Name: "",
  feedID: "",
  identifier: "",
  customerId: initialCustomerId || "",
  status: "",
  active: false,
  responsibleUser: "",
  feedDays: null,
  city: "",
  street: "",
  coordinates: null,
});

const PendingCamaraFormModal: React.FC<PendingCamaraFormModalProps> = ({
  isOpen,
  onClose,
  editingCamara,
  initialCustomerId,
  onSaved,
}) => {
  const [loading, setLoading] = useState(false);
  const [feedDaysValue, setFeedDaysValue] = useState<number | null>(null);
  const [formData, setFormData] = useState<PendingCamara>(emptyForm(initialCustomerId));

  useEffect(() => {
    if (!isOpen) return;

    if (editingCamara) {
      let initialCoords = null;
      if (editingCamara.coordinates && typeof editingCamara.coordinates === 'object') {
        const lat = Number(editingCamara.coordinates.lat);
        const lng = Number(editingCamara.coordinates.lng);
        if (!isNaN(lat) && !isNaN(lng)) initialCoords = { lat, lng };
      }
      setFormData({ ...editingCamara, coordinates: initialCoords });
      setFeedDaysValue(editingCamara.feedDays?.totalDays || null);
    } else {
      setFormData(emptyForm(initialCustomerId));
      setFeedDaysValue(null);
    }
  }, [isOpen, editingCamara, initialCustomerId]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (name === 'feedDays') {
      setFeedDaysValue(value === '' ? null : Number(value));
    } else if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: (e.target as HTMLInputElement).checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleCoordinatesChange = useCallback((coords: { lat: number; lng: number }) => {
    setFormData(prev => ({ ...prev, coordinates: coords }));
  }, []);

  const validateForm = () => {
    const errors: string[] = [];
    if (!formData.Name) errors.push("El nombre es obligatorio.");
    if (feedDaysValue === null || feedDaysValue <= 0) {
      errors.push("Los días de feed deben ser mayores a 0.");
    }
    if (!formData.coordinates) {
      errors.push("Las coordenadas son obligatorias.");
    }
    return errors;
  };

  const handleSubmit = async () => {
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      validationErrors.forEach(err => toast.error(err));
      return;
    }

    setLoading(true);
    try {
      const dataToSave = { ...formData };
      dataToSave.feedDays = {
        totalDays: feedDaysValue ?? 0,
        daysLeft: feedDaysValue ?? 0,
        createdAt: new Date().toISOString(),
      };

      if (editingCamara?.id) {
        await updateCamaraById(editingCamara.id, dataToSave);
        toast.success("Camara actualizado exitosamente!");
      } else {
        await createCamara(dataToSave);
        toast.success("Camara agregado exitosamente!");
      }

      onSaved?.();
      onClose();
    } catch (err) {
      const quotaMessage = getSlotQuotaMessage(err);
      toast.error(
        quotaMessage ?? (editingCamara?.id ? "Error al actualizar el camara" : "Error al agregar el camara"),
        quotaMessage ? { autoClose: 8000 } : undefined
      );
      console.error(err);
    }
    setLoading(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg w-full max-w-4xl my-8">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-2xl font-bold">{editingCamara ? "Editar Cámara" : "Agregar Cámara"}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
              <input type="text" name="Name" value={formData.Name} onChange={handleInputChange} className="w-full px-3 py-2 border rounded-md" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Feed ID</label>
              <input type="text" name="feedID" value={formData.feedID} onChange={handleInputChange} className="w-full px-3 py-2 border rounded-md" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">ID Cliente</label>
              <input type="text" name="customerId" value={formData.customerId} onChange={handleInputChange} className="w-full px-3 py-2 border rounded-md" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
              <select name="status" value={formData.status} onChange={handleInputChange} className="w-full px-3 py-2 border rounded-md">
                <option value="">Seleccionar estado</option>
                <option value="Creado">Creado</option>
                <option value="Aprobado">Aprobado</option>
                <option value="Terminado">Terminado</option>
                <option value="Suspendida">Suspendida</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Días de Feed *</label>
              <input type="number" name="feedDays" value={feedDaysValue ?? ''} onChange={handleInputChange} className="w-full px-3 py-2 border rounded-md" />
            </div>
            <div className="flex items-center mt-6">
              <input type="checkbox" name="active" checked={!!formData.active} onChange={handleInputChange} className="w-4 h-4 text-blue-600 rounded" />
              <label className="ml-2 text-sm font-medium text-gray-700">Generar Timelapse Automáticamente</label>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ciudad</label>
              <input type="text" name="city" value={formData.city} onChange={handleInputChange} className="w-full px-3 py-2 border rounded-md" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Calle</label>
              <input type="text" name="street" value={formData.street} onChange={handleInputChange} className="w-full px-3 py-2 border rounded-md" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Coordenadas *</label>
            <div className="h-[300px] w-full border border-gray-300 rounded-lg overflow-hidden">
              <LocationPickerMap
                initialCoordinates={formData.coordinates as any}
                onCoordinatesChange={handleCoordinatesChange}
              />
            </div>
          </div>
        </div>

        <div className="p-6 border-t flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300">Cancelar</button>
          <button onClick={handleSubmit} className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center gap-2" disabled={loading}>
            {loading ? "Guardando..." : <><Save className="w-4 h-4" /> Guardar</>}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PendingCamaraFormModal;
